import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, Search, Send, FileText, MessageSquare, ChevronDown, ChevronUp, Clock, Users, CheckCircle, X, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { ConversionAnalytics } from './ConversionAnalytics';
import { API_URL, fetchWithAuth } from './constants';

const STATUS_COLORS = {
  submitted: 'bg-blue-100 text-blue-700 border-blue-200',
  interviewing: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  offer: 'bg-amber-100 text-amber-700 border-amber-200',
  placed: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  rejected: 'bg-red-100 text-red-700 border-red-200',
  withdrawn: 'bg-gray-100 text-gray-600 border-gray-200',
};

const ALL_STATUSES = ['submitted', 'interviewing', 'offer', 'placed', 'rejected', 'withdrawn'];

export const SubmissionTracker = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [expanded, setExpanded] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(null);

  const fetchSubmissions = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (filterStatus !== 'all') params.set('status', filterStatus);
      const data = await fetchWithAuth(`${API_URL}/api/intelligence/acquisition/submissions?${params}`);
      setSubmissions(data.submissions || []);
    } catch {}
    setLoading(false);
  }, [search, filterStatus]);

  const fetchAnalytics = useCallback(async () => {
    try {
      const data = await fetchWithAuth(`${API_URL}/api/intelligence/acquisition/submissions/analytics`);
      setAnalytics(data);
    } catch {}
  }, []);

  useEffect(() => { fetchSubmissions(); fetchAnalytics(); }, [fetchSubmissions, fetchAnalytics]);

  const updateCandidateStatus = async (submissionId, candidateKey, newStatus) => {
    try {
      const token = localStorage.getItem('backoffice_token');
      const res = await fetch(`${API_URL}/api/intelligence/acquisition/submissions/${submissionId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ candidate_key: candidateKey, candidate_status: newStatus }),
      });
      const data = await res.json();
      if (data.error) { toast.error(data.detail); }
      else { toast.success(`Status updated to ${newStatus}`); fetchSubmissions(); fetchAnalytics(); }
    } catch { toast.error('Status update failed'); }
  };

  const addNote = async (submissionId) => {
    if (!noteText.trim()) return;
    setSavingNote(submissionId);
    try {
      const token = localStorage.getItem('backoffice_token');
      const res = await fetch(`${API_URL}/api/intelligence/acquisition/submissions/${submissionId}/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ text: noteText.trim() }),
      });
      const data = await res.json();
      if (data.ok) { toast.success('Note added'); setNoteText(''); fetchSubmissions(); }
      else { toast.error(data.detail || 'Failed'); }
    } catch { toast.error('Failed to add note'); }
    setSavingNote(null);
  };

  return (
    <div data-testid="submission-tracker">
      <ConversionAnalytics analytics={analytics} />

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4" data-testid="submission-filters">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input type="text" placeholder="Search company or role..." value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50"
            data-testid="submission-search" />
        </div>
        {['all', ...ALL_STATUSES].map(s => (
          <button key={s} onClick={() => setFilterStatus(s)}
            className={`px-3 py-1.5 rounded-lg text-[10px] font-bold transition-all capitalize ${filterStatus === s ? 'bg-[#D4AF37] text-[#0A1F44] shadow-sm' : 'bg-white text-gray-500 border border-gray-300 hover:bg-gray-50'}`}
            data-testid={`filter-status-${s}`}>
            {s}
          </button>
        ))}
        <span className="text-xs text-gray-400 ml-2">{submissions.length} submissions</span>
      </div>

      {/* Submissions List */}
      {loading ? (
        <div className="flex items-center justify-center py-12 text-gray-400"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading submissions...</div>
      ) : submissions.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
          <Send className="h-10 w-10 text-gray-200 mx-auto mb-3" />
          <p className="text-sm text-gray-500">No submissions yet</p>
          <p className="text-xs text-gray-400 mt-1">Generate a pitch from the Candidate Pipelines tab and submit to get started</p>
        </div>
      ) : (
        <div className="space-y-3">
          {submissions.map(sub => {
            const isExpanded = expanded === sub.submission_id;
            const candidateCount = (sub.candidates || []).length;
            const daysSince = Math.floor((Date.now() - new Date(sub.created_at).getTime()) / 86400000);

            return (
              <div key={sub.submission_id} className={`bg-white border rounded-xl overflow-hidden transition-all ${isExpanded ? 'border-[#D4AF37] shadow-md' : 'border-gray-200'}`}
                data-testid={`submission-${sub.submission_id}`}>
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-3 cursor-pointer hover:bg-gray-50/50"
                  onClick={() => setExpanded(isExpanded ? null : sub.submission_id)}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-9 w-9 bg-[#0A1F44] rounded-lg flex items-center justify-center flex-shrink-0">
                      <Send className="h-4 w-4 text-[#D4AF37]" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-[#0A1F44] truncate">{sub.client_company}</span>
                        <span className={`px-2 py-0.5 text-[9px] font-bold rounded border capitalize ${STATUS_COLORS[sub.status] || STATUS_COLORS.submitted}`}>
                          {sub.status}
                        </span>
                        {sub.playbook_used && (
                          <span className="px-1.5 py-0.5 text-[7px] font-bold rounded bg-[#D4AF37]/15 text-[#8B7425] border border-[#D4AF37]/30 truncate max-w-[120px]">{sub.playbook_used}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-[10px] text-gray-500 mt-0.5">
                        <span>{sub.role_title}</span>
                        <span>{candidateCount} candidate{candidateCount !== 1 ? 's' : ''}</span>
                        <span>Avg score: {sub.avg_candidate_score}</span>
                        <span className="flex items-center gap-0.5"><Clock className="h-2.5 w-2.5" /> {daysSince === 0 ? 'Today' : `${daysSince}d ago`}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-[10px] text-gray-400">{sub.submission_id}</span>
                    {isExpanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                  </div>
                </div>

                {/* Expanded */}
                {isExpanded && (
                  <div className="border-t border-gray-100 px-5 py-4 bg-gray-50/30 space-y-4">
                    {/* Candidates with status */}
                    <div>
                      <p className="text-[10px] text-gray-500 uppercase tracking-wider font-medium mb-2">
                        <Users className="h-3 w-3 inline mr-1" /> Candidates
                      </p>
                      <div className="space-y-2">
                        {(sub.candidates || []).map((c, ci) => (
                          <div key={ci} className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg"
                            data-testid={`sub-candidate-${ci}`}>
                            <div className="flex items-center gap-3">
                              <div className={`h-7 w-7 rounded-full flex items-center justify-center ${c.fit_score >= 50 ? 'bg-emerald-100' : c.fit_score >= 30 ? 'bg-amber-100' : 'bg-gray-100'}`}>
                                <span className={`text-[10px] font-bold ${c.fit_score >= 50 ? 'text-emerald-700' : c.fit_score >= 30 ? 'text-amber-700' : 'text-gray-600'}`}>{c.fit_score}</span>
                              </div>
                              <div>
                                <span className="text-xs font-semibold text-[#0A1F44]">{c.name}</span>
                                <p className="text-[10px] text-gray-500">{c.title} at {c.current_company}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <select value={c.status || 'submitted'}
                                onChange={e => updateCandidateStatus(sub.submission_id, c.candidate_key, e.target.value)}
                                className={`px-2 py-1 text-[10px] font-bold rounded-lg border cursor-pointer appearance-none ${STATUS_COLORS[c.status] || STATUS_COLORS.submitted}`}
                                style={{ backgroundImage: 'none', paddingRight: '8px' }}
                                data-testid={`candidate-status-${ci}`}>
                                {ALL_STATUSES.map(s => (
                                  <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Status History */}
                    {(sub.status_history || []).length > 0 && (
                      <div>
                        <p className="text-[10px] text-gray-500 uppercase tracking-wider font-medium mb-1.5">Activity Trail</p>
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                          {[...(sub.status_history || [])].reverse().map((h, hi) => (
                            <div key={hi} className="flex items-center gap-2 text-[10px] p-1.5 bg-white rounded border border-gray-100">
                              <CheckCircle className="h-3 w-3 text-gray-400 flex-shrink-0" />
                              <span className="font-medium text-[#0A1F44] capitalize">{h.status}</span>
                              <span className="text-gray-400">by {h.actor}</span>
                              <span className="text-gray-300 ml-auto">{new Date(h.timestamp).toLocaleDateString()} {new Date(h.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Pitch Preview */}
                    {sub.pitch_text && (
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-[10px] text-gray-500 uppercase tracking-wider font-medium">
                            <FileText className="h-3 w-3 inline mr-1" /> Pitch ({sub.pitch_type})
                          </p>
                          <button onClick={() => { navigator.clipboard.writeText(sub.pitch_text); toast.success('Pitch copied'); }}
                            className="text-[9px] text-gray-500 hover:text-[#0A1F44]" data-testid={`copy-pitch-${sub.submission_id}`}>
                            Copy
                          </button>
                        </div>
                        <div className="p-3 bg-white border border-gray-200 rounded-lg text-xs text-gray-700 whitespace-pre-wrap max-h-40 overflow-y-auto leading-relaxed">{sub.pitch_text}</div>
                      </div>
                    )}

                    {/* Notes */}
                    <div>
                      <p className="text-[10px] text-gray-500 uppercase tracking-wider font-medium mb-1.5">
                        <MessageSquare className="h-3 w-3 inline mr-1" /> Notes
                      </p>
                      {(sub.notes || []).length > 0 && (
                        <div className="space-y-1 mb-2 max-h-24 overflow-y-auto">
                          {[...(sub.notes || [])].reverse().map((n, ni) => (
                            <div key={ni} className="flex items-start gap-2 text-[10px] p-2 bg-amber-50 rounded border border-amber-100">
                              <span className="text-gray-700">{n.text}</span>
                              <span className="text-gray-400 ml-auto whitespace-nowrap">{n.actor} — {new Date(n.timestamp).toLocaleDateString()}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="flex items-center gap-1">
                        <input type="text" value={noteText} onChange={e => setNoteText(e.target.value)}
                          placeholder="Add a note..."
                          className="flex-1 px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
                          onKeyDown={e => { if (e.key === 'Enter') addNote(sub.submission_id); }}
                          data-testid={`note-input-${sub.submission_id}`} />
                        <button onClick={() => addNote(sub.submission_id)} disabled={savingNote === sub.submission_id || !noteText.trim()}
                          className="px-3 py-1.5 bg-[#0A1F44] text-white text-[10px] font-bold rounded-lg disabled:opacity-50"
                          data-testid={`note-save-${sub.submission_id}`}>
                          {savingNote === sub.submission_id ? '...' : 'Save'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
